import {
  Button,
  Fieldset,
  Flex,
  Input,
  Radio,
  RadioGroupField,
  TextAreaField,
} from "@aws-amplify/ui-react"
import {
  JoinOrgRequestMessageMaxLength,
  JoinOrgRequestOrgHintMaxLength,
  OrganizationSchemaNameMaxLength,
  UserErrorCodes,
  UserNameMaxLength,
} from "@strtmn/sds/schemas"
import { getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useState } from "react"
import {
  Controller,
  useForm,
  type RegisterOptions,
  type SubmitHandler,
} from "react-hook-form"
import { useNavigate, useRevalidator } from "react-router-dom"
import { registerCurrentUser, requestJoinOrg } from "../../api/userData"
import { RoundedCard } from "../../components"
import { getLinkToOrgById } from "../../LinkUtils"

type Mode = "create-org" | "join-org"

type FormValues = {
  first_name: string
  last_name: string
  mode: Mode
  org: {
    name: string
    address: {
      street: string
      number: string
      zip: string
      city: string
      country: string
    }
  }
  org_hint: string
  message: string
}

/** Shows the server's message; the API wraps it as `{ body: { error | message } }`. */
const toErrorMessage = (error: unknown) => {
  const body = (error as any)?.body
  return (
    body?.error ??
    body?.message ??
    (typeof body === "string" ? body : getErrorMessage(error))
  )
}

/** Required, not only whitespace, and at most `max` characters. */
const requiredText = (
  requiredMessage: string,
  max: number
): RegisterOptions<FormValues, any> => ({
  required: requiredMessage,
  validate: (value: string) => value.trim().length > 0 || requiredMessage,
  maxLength: { value: max, message: `Max. ${max} Zeichen.` },
})

/** Address rows: stacked on phones, side by side from Amplify's `small` breakpoint. */
const addressRowDisplay = { base: "contents", small: "flex" } as const
/** The wide field of an address row takes the remaining space. */
const addressWideField = {
  flex: { base: "initial", small: "1" },
  minWidth: "0", // lets the input shrink below its intrinsic width
} as const
/** The short fields (Nr., PLZ) share one width, so the rows line up. */
const addressShortField = { width: { base: "auto", small: "7rem" } } as const

/**
 * Registration of a signed-in Cognito user without a local user: name, and
 * either a new org or a request to join an existing one.
 * Rendered by the app shell instead of the page content.
 */
export default function Registration() {
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const [isJoinRequested, setIsJoinRequested] = useState(false)
  const {
    control,
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      first_name: "",
      last_name: "",
      mode: "create-org",
      org: {
        name: "",
        address: { street: "", number: "", zip: "", city: "", country: "" },
      },
      org_hint: "",
      message: "",
    },
  })
  const mode = watch("mode")

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const names = {
      first_name: data.first_name.trim(),
      last_name: data.last_name.trim(),
    }
    try {
      if (data.mode === "create-org") {
        const address = data.org.address
        const orgId = await registerCurrentUser({
          ...names,
          org: {
            name: data.org.name.trim(),
            address: {
              street: address.street.trim(),
              number: address.number.trim(),
              zip: address.zip.trim(),
              city: address.city.trim(),
              country: address.country.trim(),
              gps: null,
            },
          },
        })
        if (orgId) {
          // Land on the new org, not on whatever URL the user had opened
          // before registering — the menu's org links pointed at
          // `/orgs/undefined/...` back then. `replace` drops that URL from
          // the history. The navigation re-runs the app loader, which picks
          // up the now-registered user.
          navigate(getLinkToOrgById(orgId, false), { replace: true })
        } else {
          // Not signed in any more; let the app shell decide what to show
          revalidator.revalidate()
        }
      } else {
        await requestJoinOrg({
          ...names,
          org_hint: data.org_hint.trim(),
          message: data.message?.trim() || undefined,
        })
        setIsJoinRequested(true)
      }
    } catch (error) {
      if ((error as any)?.body?.code === UserErrorCodes.AlreadyRegistered) {
        // Registered already, e.g. in another tab
        revalidator.revalidate()
        return
      }
      setError("root.serverError", { message: toErrorMessage(error) })
    }
  }

  if (isJoinRequested) {
    return (
      <main className="p-4 md:p-8 max-w-xl">
        <RoundedCard>
          <h2>{I18n.get("Vielen Dank!")}</h2>
          <p>
            {I18n.get(
              "Der Kundensupport wurde informiert und wird sich bei Ihnen melden."
            )}
          </p>
          <Button
            variation="primary"
            size="small"
            className="!mt-4"
            onClick={() => revalidator.revalidate()}
          >
            {I18n.get("Weiter")}
          </Button>
        </RoundedCard>
      </main>
    )
  }

  return (
    <main className="p-4 md:p-8 max-w-xl">
      <RoundedCard>
        <h2>{I18n.get("Willkommen!")}</h2>
        <p>
          {I18n.get(
            "Bevor es losgeht, benötigen wir noch ein paar Angaben von Ihnen."
          )}
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset
            legend={I18n.get("Ihr Name")}
            variation="outlined"
            size="small"
          >
            <Input
              type="text"
              size="small"
              placeholder={I18n.get("Vorname")}
              autoComplete="given-name"
              {...register(
                "first_name",
                requiredText(
                  I18n.get("Geben Sie Ihren Vornamen an."),
                  UserNameMaxLength
                )
              )}
              className={errors.first_name ? "error" : ""}
            />
            {errors.first_name && (
              <div className="error">{errors.first_name.message}</div>
            )}
            <Input
              type="text"
              size="small"
              placeholder={I18n.get("Nachname")}
              autoComplete="family-name"
              {...register(
                "last_name",
                requiredText(
                  I18n.get("Geben Sie Ihren Nachnamen an."),
                  UserNameMaxLength
                )
              )}
              className={errors.last_name ? "error" : ""}
            />
            {errors.last_name && (
              <div className="error">{errors.last_name.message}</div>
            )}
          </Fieldset>

          <Fieldset
            legend={I18n.get("Ihr Betrieb")}
            variation="outlined"
            size="small"
            className="!mt-4"
          >
            <Controller
              control={control}
              name="mode"
              render={({ field }) => (
                <RadioGroupField
                  legend={I18n.get("Ihr Betrieb")}
                  legendHidden
                  size="small"
                  name={field.name}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value as Mode)}
                >
                  <Radio value="create-org">
                    {I18n.get("Ich lege einen neuen Betrieb an.")}
                  </Radio>
                  <Radio value="join-org">
                    {I18n.get(
                      "Ich möchte einem bestehenden Betrieb beitreten."
                    )}
                  </Radio>
                </RadioGroupField>
              )}
            />

            {mode === "create-org" ? (
              <>
                <Input
                  type="text"
                  size="small"
                  placeholder={I18n.get("Name des Betriebs")}
                  {...register("org.name", {
                    ...requiredText(
                      I18n.get("Geben Sie den Namen des Betriebs an."),
                      OrganizationSchemaNameMaxLength
                    ),
                    // Hidden in "join-org" mode: must not block the submit
                    shouldUnregister: true,
                  })}
                  className={errors.org?.name ? "error" : ""}
                />
                {errors.org?.name && (
                  <div className="error">{errors.org.name.message}</div>
                )}
                {/* Two rows: a wide field and a short one of fixed width, so
                    the edges line up. On phones, `display: contents` dissolves
                    the row, and its fields stack like the other Fieldset
                    children, with the Fieldset's own spacing. */}
                <Flex direction="row" display={addressRowDisplay}>
                  <Input
                    type="text"
                    size="small"
                    placeholder={I18n.get("Straße")}
                    autoComplete="address-line1"
                    {...addressWideField}
                    {...register("org.address.street")}
                  />
                  <Input
                    type="text"
                    size="small"
                    placeholder={I18n.get("Nr.")}
                    maxLength={6}
                    {...addressShortField}
                    {...register("org.address.number")}
                  />
                </Flex>
                <Flex direction="row" display={addressRowDisplay}>
                  <Input
                    type="text"
                    size="small"
                    placeholder={I18n.get("PLZ")}
                    autoComplete="postal-code"
                    maxLength={8}
                    {...addressShortField}
                    {...register("org.address.zip")}
                  />
                  <Input
                    type="text"
                    size="small"
                    placeholder={I18n.get("Ort")}
                    autoComplete="address-level2"
                    {...addressWideField}
                    {...register("org.address.city")}
                  />
                </Flex>
                <Input
                  type="text"
                  size="small"
                  placeholder={I18n.get("Land")}
                  autoComplete="country-name"
                  {...register("org.address.country")}
                />
              </>
            ) : (
              <>
                <Input
                  type="text"
                  size="small"
                  placeholder={I18n.get("Name und Ort des Betriebs")}
                  {...register("org_hint", {
                    ...requiredText(
                      I18n.get(
                        "Geben Sie an, welchem Betrieb Sie beitreten möchten."
                      ),
                      JoinOrgRequestOrgHintMaxLength
                    ),
                    // Hidden in "create-org" mode: must not block the submit
                    shouldUnregister: true,
                  })}
                  className={errors.org_hint ? "error" : ""}
                />
                {errors.org_hint && (
                  <div className="error">{errors.org_hint.message}</div>
                )}
                <TextAreaField
                  label={I18n.get("Nachricht an den Kundensupport (optional)")}
                  size="small"
                  rows={3}
                  {...register("message", {
                    shouldUnregister: true,
                    maxLength: {
                      value: JoinOrgRequestMessageMaxLength,
                      message: `Max. ${JoinOrgRequestMessageMaxLength} Zeichen.`,
                    },
                  })}
                  hasError={!!errors.message}
                  errorMessage={errors.message?.message}
                />
              </>
            )}
          </Fieldset>

          {errors.root?.["serverError"] && (
            <p className="error">{errors.root["serverError"].message}</p>
          )}

          <Button
            type="submit"
            variation="primary"
            size="small"
            className="!mt-4"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText={I18n.get("Wird gespeichert...")}
          >
            {mode === "create-org"
              ? I18n.get("Betrieb anlegen")
              : I18n.get("Anfrage senden")}
          </Button>
        </form>
      </RoundedCard>
    </main>
  )
}
